import {
  Drawer,
  Space,
  Button,
  Row,
  Col,
  Card,
  Statistic,
  Tag,
  Divider,
} from "antd";
import { ResponseInventoryControlRegistry } from "api/generated";
import moment from "moment";
import { areEquals, volumeFormatter } from "services/utils/formatterUtil";

interface StockDifferenceViewerProps {
  periodRecord?: ResponseInventoryControlRegistry;
  onClose: () => void;
  isDetailsOpen: boolean;
}

interface ComparisonItem {
  title: string;
  currentValue: number | undefined;
  operativeValue: number | undefined;
  isEqual: boolean;
  details?: string;
}

function StockDifferenceViewer({
  periodRecord,
  onClose,
  isDetailsOpen,
}: StockDifferenceViewerProps) {
  const getComparisonData = (
    record: ResponseInventoryControlRegistry,
  ): ComparisonItem[] => {
    if (!record) return [];

    // Calcular total de recepciones operativas
    const calculatedTotalInputs =
      (record.calculatedTotalReceptions || 0) +
      (record.calculatedTotalTraspasos || 0);

    return [
      {
        title: "Volumen Inicial",
        currentValue: record.initialVolume,
        operativeValue: record.initialVolume,
        isEqual: true,
        details: "Ultimo valor de inventarios del mes",
      },
      {
        title: "Total de Entradas",
        currentValue: record.totalInputs,
        operativeValue: calculatedTotalInputs,
        isEqual: areEquals(record.totalInputs, calculatedTotalInputs),
        details: `Recepciones (${
          record.calculatedTotalReceptions || 0
        }) + Traspasos (${record.calculatedTotalTraspasos || 0})`,
      },
      {
        title: "Total de Salidas",
        currentValue: record.totalOutputs,
        operativeValue: record.calculatedTotalSales,
        isEqual: areEquals(record.totalOutputs, record.calculatedTotalSales),
        details: "Ventas (Movimientos)",
      },
      {
        title: "Volumen Calculado",
        currentValue: record.finalVolume,
        operativeValue: record.calculatedFinalVolume,
        isEqual: areEquals(record.finalVolume, record.calculatedFinalVolume),
        details: "Volumen final calculado",
      },
      {
        title: "Inventario Fisico",
        currentValue: record.finalStaticVolume,
        operativeValue: record.calculatedLatestMeasurementVolume,
        isEqual: areEquals(
          record.finalStaticVolume,
          record.calculatedLatestMeasurementVolume,
        ),
        details: "Medición física",
      },
      {
        title: "Litros de Diferencia",
        currentValue: record.volumeDifference,
        operativeValue: record.calculatedVolumeDifference,
        isEqual: areEquals(
          record.volumeDifference,
          record.calculatedVolumeDifference,
        ),
        details: "Diferencia entre valores",
      },
      {
        title: "Porcentaje de Diferencia",
        currentValue: record.percentageDifference,
        operativeValue: record.calculatedPercentageDifference,
        isEqual: areEquals(
          record.percentageDifference,
          record.calculatedPercentageDifference,
        ),
        details: "Porcentaje de diferencia",
      },
    ];
  };

  const comparisonData = periodRecord ? getComparisonData(periodRecord) : [];

  return (
    <Drawer
      title={`Detalles de control de existencias para el periodo: ${moment(
        periodRecord?.registeredDate,
      )
        .locale("es")
        .format("MMMM YYYY")}`}
      height="100%"
      placement="bottom"
      onClose={onClose}
      open={isDetailsOpen}
      styles={{
        mask: {
          backdropFilter: "blur(10px)",
        },
        wrapper: {
          minWidth: "-webkit-fill-available",
        },
      }}
      footer={
        <Space>
          <Button onClick={onClose} className="back-button">
            Cerrar
          </Button>
        </Space>
      }
      width="90%"
    >
      {periodRecord && (
        <div>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Punto de Distribución"
                  value={periodRecord.distributionPointName}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Número de Permiso"
                  value={periodRecord.distributionPointSenerLicense || "N/A"}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Fecha del Registro"
                  value={moment(periodRecord.registeredDate).format(
                    "MMMM YYYY",
                  )}
                />
              </Card>
            </Col>
          </Row>

          <h3></h3>
          <Divider style={{ borderColor: "#3bbedfff" }}>
            Control de Existencias / Datos Operativos
          </Divider>
          <Row gutter={16}>
            {comparisonData.map((item, index) => (
              <Col
                xs={24}
                sm={12}
                lg={8}
                key={index}
                style={{ marginBottom: 16 }}
              >
                <Card
                  size="small"
                  title={item.title}
                  extra={
                    <Tag color={item.isEqual ? "green" : "red"}>
                      {item.isEqual ? "Consistente" : "Inconsistencia"}
                    </Tag>
                  }
                >
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div>
                      <div style={{ fontSize: "12px", color: "#888" }}>
                        Valor en el registro
                      </div>
                      <div style={{ fontWeight: "bold" }}>
                        {volumeFormatter(item.currentValue)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: "12px", color: "#888" }}>
                        Valor Operativo
                      </div>
                      <div style={{ fontWeight: "bold" }}>
                        {volumeFormatter(item.operativeValue)}
                      </div>
                    </div>
                  </div>
                  {item.details && (
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "12px",
                        color: "#666",
                      }}
                    >
                      {item.details}
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}
    </Drawer>
  );
}

export default StockDifferenceViewer;
